import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useGraphql } from "~/api-client";
import {
  HOT_DEALS_CATEGORY_ID_PREFIX,
  OrderDispatchOptionToFulfillmentType,
} from "~/constants";
import type {
  LocationDeal,
  OptionChoiceDef,
  ProductDef,
  ProductOptionDef,
} from "~/graphql-client/generated";
import { useFeatures } from "~/hooks/features";
import { useLocation } from "~/hooks/locations";
import { useBaseMenu, useMenu } from "~/hooks/menu-v2";
import type {
  CartItem,
  OptionChoice,
  OrderDispatchOption,
  Product,
  ProductOption,
  TimeSlot,
} from "~/types";
import {
  dealWithinPeriod,
  dealWithinStartEndWindow,
  getConflictingDeals,
} from "~/utils/deal";

const EMPTY_DEALS: LocationDeal[] = [];
const EMPTY_LEGACY_DEALS: Product[] = [];

export function useLegacyDeals(
  dispatchOption: OrderDispatchOption | null,
  timeSlot: TimeSlot | null,
): { legacyDeals: Product[]; loading: boolean } {
  const { menu, isLoading } = useBaseMenu(dispatchOption, timeSlot);

  const legacyDeals = useMemo(
    () =>
      menu.find((c) => c.id.startsWith(HOT_DEALS_CATEGORY_ID_PREFIX))
        ?.products ?? EMPTY_LEGACY_DEALS,
    [menu],
  );

  return { legacyDeals, loading: isLoading };
}

export function useLocationDeals(
  locationRef: string | undefined,
  dispatchOption: OrderDispatchOption | null,
) {
  const graphql = useGraphql();
  const { isFeatureEnabled, isLoading: loadingFeatures } =
    useFeatures(locationRef);
  const newDealsEnabled = isFeatureEnabled("web.deals-new");
  const { location, loading: loadingLocation } = useLocation();

  const {
    data,
    isLoading: loadingDeals,
    error,
  } = useQuery({
    queryKey: ["location-deals", locationRef, newDealsEnabled],
    queryFn: () =>
      locationRef && newDealsEnabled
        ? graphql.getLocationDeals(locationRef)
        : Promise.resolve({ locationDeals: [] }),
    refetchOnWindowFocus: true,
  });

  const timeZone = location?.timezone;
  const loading = loadingDeals || loadingLocation || loadingFeatures;

  const deals = useMemo(() => {
    if (loading || !timeZone) return EMPTY_DEALS;

    const allDeals = data?.locationDeals ?? EMPTY_DEALS;

    return allDeals.filter(
      (deal) =>
        dealWithinStartEndWindow(deal, timeZone) &&
        dealWithinPeriod(deal, timeZone) &&
        // NOTE: We filter out deals by fulfillment type, to match existing checkmate deals behaviour.
        // We could actually show these in the menu and guide the customer to switch fulfillment type for a different UX.
        dealMatchesFulfillmentType(deal, dispatchOption),
    );
  }, [data?.locationDeals, timeZone, loading, dispatchOption]);

  return {
    deals,
    loading,
    error,
  };
}

export function useDealsByDispatchOption(locationRef: string | undefined) {
  const { deals, loading, error } = useLocationDeals(locationRef, null);

  const getDeals = useCallback(
    (dispatchOption: OrderDispatchOption | null) =>
      deals.filter((deal) => dealMatchesFulfillmentType(deal, dispatchOption)),
    [deals],
  );

  return { getDeals, loading, error };
}

function dealMatchesFulfillmentType(
  deal: LocationDeal,
  dispatchOption: OrderDispatchOption | null | undefined,
): boolean {
  if (!dispatchOption) return true;
  if (!deal.fulfillmentType) return true;

  const fulfillmentType = OrderDispatchOptionToFulfillmentType[dispatchOption];

  return deal.fulfillmentType.includes(fulfillmentType);
}

/**
 * Derives the exclusive-deal conflict for adding `newDeal` to the cart: whether there's a
 * conflict and the distinct deals to display in the conflict dialog. The actual removal is
 * handled by the cart's addItem. Pass cart items and deals in (rather than calling
 * useCart/useDeals here) so callers that already hold those don't run those hooks twice.
 */
export function useDealConflict(
  cartItems: CartItem[],
  deals: LocationDeal[],
  legacyDeals: Product[],
  newDealSelected: boolean,
  newDealExclusive: boolean,
) {
  const conflict = useMemo(
    () =>
      newDealSelected
        ? getConflictingDeals(cartItems, deals, legacyDeals, newDealExclusive)
        : null,
    [cartItems, deals, legacyDeals, newDealSelected, newDealExclusive],
  );

  return {
    hasConflict: (conflict?.count ?? 0) >= 1,
    dealsToRemove: conflict?.deals ?? [],
  };
}

export function useLocationDeal(
  locationRef: string,
  dispatchOption: OrderDispatchOption | null,
  id: string | null,
): { deal: LocationDeal | null; loading: boolean } {
  const { deals, loading } = useLocationDeals(locationRef, dispatchOption);

  const deal = deals?.find((deal) => deal.id === id) ?? null;

  if (!deal) {
    return { deal: null, loading };
  }

  return {
    deal,
    loading,
  };
}

export function useLocationDealProduct(
  locationRef: string,
  id: string | null,
  dispatchOption: OrderDispatchOption | null,
  timeSlot: TimeSlot | null,
): {
  dealAsProduct: Product | null;
  loading: boolean;
} {
  const { deal, loading: loadingDeal } = useLocationDeal(
    locationRef,
    dispatchOption,
    id,
  );
  const { isLoading: loadingMenu, menu } = useMenu(dispatchOption, timeSlot);

  if (!deal) {
    return { dealAsProduct: null, loading: loadingDeal || loadingMenu };
  }

  const products = menu.flatMap((cat) => cat.products ?? []);

  const dealAsProduct = dealToProduct(deal, products);
  return { dealAsProduct, loading: loadingDeal || loadingMenu };
}

export function dealToProduct(
  deal: LocationDeal,
  products: Product[],
): Product {
  const tags = ["deal"];

  if (deal.hidden) {
    tags.push("ui-hint:product:hidden");
  }

  return {
    id: deal.id,
    plu: deal.plu,
    title: deal.title,
    description: deal.description ?? undefined,
    imageUrl: deal.imageUrl ?? undefined,
    price: { amount: 0, taxIncluded: 0 },
    options: deal.options.map((option) => ({
      id: option.id,
      title: option.title,
      maxQuantity: option.maxQuantity,
      minQuantity: option.minQuantity,
      choices: (option.choices ?? []).map((choice) => ({
        product: productDefToProduct(choice.productDef, products) ?? {
          id: "__unknown__", // TODO: filter these out? Set deal to unavailable?
          title: "Product Not Found",
          price: { amount: 0 },
          available: false,
          tags: [],
        },
        minQuantity: choice.minQuantity,
        maxQuantity: choice.maxQuantity,
        defaultQuantity: choice.defaultQuantity,
      })),
    })),
    available: true,
    tags,
  };
}

// Builds a Product for a deal from the deal definition alone — no menu
// resolution needed. It carries what pooling/completeness checks use: each
// option's min/max quantities and its allowed choices (by productDef id),
// recursively including each choice product's own option constraints. A choice
// product with `options: undefined` means the deal doesn't restrict its config
// (any valid configuration is allowed); a defined (possibly empty) options list
// means the deal restricts it to exactly those. Not for pricing or display.
function productDefToPoolingProduct(def: ProductDef): Product {
  return {
    id: def.id,
    title: def.title ?? "",
    price: { amount: 0, taxIncluded: 0 },
    available: true,
    tags: [],
    options: def.options
      ? def.options.map(optionDefToPoolingOption)
      : undefined,
  };
}

function optionDefToPoolingOption(option: ProductOptionDef): ProductOption {
  return {
    id: option.id,
    title: option.title ?? "",
    minQuantity: option.minQuantity,
    maxQuantity: option.maxQuantity,
    choices: (option.choices ?? []).map((choice) => ({
      product: productDefToPoolingProduct(choice.productDef),
      minQuantity: choice.minQuantity,
      maxQuantity: choice.maxQuantity,
      defaultQuantity: choice.defaultQuantity,
    })),
  };
}

export function dealToPoolingProduct(deal: LocationDeal): Product {
  return {
    id: deal.id,
    title: deal.title,
    price: { amount: 0, taxIncluded: 0 },
    available: true,
    tags: ["deal"],
    options: deal.options.map((option) => ({
      id: option.id,
      title: option.title,
      minQuantity: option.minQuantity,
      maxQuantity: option.maxQuantity,
      choices: (option.choices ?? []).map((choice) => ({
        product: productDefToPoolingProduct(choice.productDef),
        minQuantity: choice.minQuantity,
        maxQuantity: choice.maxQuantity,
        defaultQuantity: choice.defaultQuantity,
      })),
    })),
  };
}

// Maps a productDef onto the matching Product from the menu, applying the
// def's props as overrides. Returns null when no matching product exists or
// the def is invalid, letting the caller decide on a fallback.
function productDefToProduct(
  productDef: ProductDef,
  products: Product[],
): Product | null {
  const matchedProduct = products.find((p) => p.id === productDef.id);

  if (!matchedProduct) {
    return null;
  }

  // Any props on productDef override the corresponding props on the matched
  // Product. Only override with values the def actually specifies.
  const product: Product = { ...matchedProduct };

  if (productDef.title) {
    product.title = productDef.title;
  }

  if (productDef.options) {
    const options = mapOptionDefs(
      productDef.options,
      matchedProduct.options ?? [],
    );

    // Any option on the def must exist on the matched product; if not, the
    // def is invalid and should be ignored.
    if (!options) {
      return null;
    }

    product.options = options;
  }

  return product;
}

// Maps each ProductOptionDef onto its matching ProductOption. Returns null if
// any def references an option that does not exist on the matched product.
function mapOptionDefs(
  optionDefs: ProductOptionDef[],
  matchedOptions: ProductOption[],
): ProductOption[] | null {
  const options: ProductOption[] = [];

  for (const optionDef of optionDefs) {
    const matchedOption = matchedOptions.find((o) => o.id === optionDef.id);

    if (!matchedOption) {
      return null;
    }

    options.push(optionDefToOption(optionDef, matchedOption));
  }

  return options;
}

// ProductOptionDef props override those on the matched ProductOption.
function optionDefToOption(
  optionDef: ProductOptionDef,
  matchedOption: ProductOption,
): ProductOption {
  const option: ProductOption = {
    ...matchedOption,
    minQuantity: optionDef.minQuantity,
  };

  if (optionDef.title) {
    option.title = optionDef.title;
  }

  if (optionDef.maxQuantity != null) {
    option.maxQuantity = optionDef.maxQuantity;
  }

  if (optionDef.choices) {
    option.choices = mapChoiceDefs(optionDef.choices, matchedOption.choices);
  }

  return option;
}

// Resolves each OptionChoiceDef's productDef against the products offered by
// the matched option, dropping any choice whose product def can't be resolved.
function mapChoiceDefs(
  choiceDefs: OptionChoiceDef[],
  matchedChoices: OptionChoice[],
): OptionChoice[] {
  const choiceProducts = matchedChoices.map((c) => c.product);

  return choiceDefs.flatMap((choiceDef) => {
    const product = productDefToProduct(choiceDef.productDef, choiceProducts);

    if (!product) {
      return [];
    }

    return [
      {
        product,
        minQuantity: choiceDef.minQuantity,
        maxQuantity: choiceDef.maxQuantity,
        defaultQuantity: choiceDef.defaultQuantity,
      },
    ];
  });
}
